import cn from 'clsx';
import * as RadioGroup from '@radix-ui/react-radio-group';
import {forwardRef, useEffect, useState} from 'react';
import {useProductProvider} from '@components/product/ProductProvider';
import {
  customSortByEmptyThenLoaded,
  customSortEnclosuresBySingleDualTriple,
  sortVariantOptions
} from '@helpers/common/array.mjs';

export const ProductVariantSelector = () => {
  const {selectedVariantSku, hasNoVariants, showMultiLevelVariantOptions} = useProductProvider();
  // console.log("[ProductVariantSelector] selectedVariantSku", selectedVariantSku)

  if (hasNoVariants || !selectedVariantSku) return null;
  // console.log('[showMultiLevelVariantOptions]', showMultiLevelVariantOptions);

  if (showMultiLevelVariantOptions) {
    return <MultiLevelVariantSelector />;
  }

  // console.log('[SingleLevelVariantSelector]: isLoaded', true);
  return <SingleLevelVariantSelector />;
};

const MultiLevelVariantSelector = () => {
  const [isWideFormat, setIsWideFormat] = useState(false);

  let {
    category,
    variantOptionOneName,
    variantOptionTwoName,
    variantOptionThreeName,
    selectedOptionOne,
    selectedOptionTwo,
    selectedOptionThree,
    setSelectedOptionOne,
    setSelectedOptionTwo,
    setSelectedOptionThree,
    handleOptionOneChange,
    handleOptionTwoChange,
    handleOptionThreeChange,
    optionsStatus
  } = useProductProvider();
  // console.log('[MultiLevelVariantSelector] optionsStatus', optionsStatus);

  useEffect(() => {
    const uniqueOptionOneValues = Object.keys(optionsStatus.optionOne);
    if (uniqueOptionOneValues?.length === 1) {
      setSelectedOptionOne(uniqueOptionOneValues[0]);
    }
  }, [optionsStatus.optionOne, setSelectedOptionOne]);

  useEffect(() => {
    const uniqueOptionTwoValues = Object.keys(optionsStatus.optionTwo);
    if (uniqueOptionTwoValues?.length === 1) {
      setSelectedOptionTwo(uniqueOptionTwoValues[0]);
    }
  }, [optionsStatus.optionTwo, setSelectedOptionTwo]);

  useEffect(() => {
    const uniqueOptionThreeValues = Object.keys(optionsStatus.optionThree);
    if (uniqueOptionThreeValues?.length === 1) {
      setSelectedOptionThree(uniqueOptionThreeValues[0]);
    }
  }, [optionsStatus.optionThree, setSelectedOptionThree]);

  useEffect(() => {
    const checkOptions = () => {
      const optionValues = Object.keys(optionsStatus.optionTwo);
      if (optionValues?.length === 2 && optionValues.every((value) => value?.length <= 22)) {
        setIsWideFormat(true);
      } else {
        setIsWideFormat(false);
      }
    };

    // Initial check
    checkOptions();
  }, [optionsStatus.optionTwo]);

  const noOptionOneValues = Object.keys(optionsStatus.optionOne).length === 0;
  const noOptionTwoValues = Object.keys(optionsStatus.optionTwo).length === 0;
  const noOptionThreeValues = Object.keys(optionsStatus.optionThree).length === 0;

  const optionOneKeyValues = Object.keys(optionsStatus.optionOne)
    ? customSortByEmptyThenLoaded(Object.keys(optionsStatus.optionOne))
    : [];
  const optionTwoKeyValues = Object.entries(optionsStatus.optionTwo)
    ? customSortEnclosuresBySingleDualTriple(Object.entries(optionsStatus.optionTwo))
    : [];
  const optionThreeKeyValues = Object.entries(optionsStatus.optionThree)
    ? customSortEnclosuresBySingleDualTriple(Object.entries(optionsStatus.optionThree))
    : [];
  // console.log('[MultiLevelVariantSelector] optionTwoKeyValues', optionTwoKeyValues);

  const renderOptionOne = !noOptionOneValues;
  const renderOptionTwo = !noOptionTwoValues && optionTwoKeyValues?.length > 1;
  const renderOptionThree = !noOptionThreeValues;

  return (
    <div className="space-y-2 py-2 bg-white rounded-md">
      {renderOptionOne && (
        <div>
          {variantOptionOneName && (
            <div className="text-[16px] text-gray-800 mb-1">{variantOptionOneName}</div>
          )}
          <RadioGroup.Root
            value={selectedOptionOne}
            name="variantOptionOne"
            onValueChange={handleOptionOneChange}
            className="flex flex-wrap gap-2 md:w-[85%]">
            {optionOneKeyValues?.map((optionOneValue) => {
              let style = {
                flexGrow: '1',
                flexShrink: '0',
                flexBasis: 'calc(33.33% - 0.5rem)'
              };

              if (category === 'subwoofers') {
                style.flexGrow = '0';
                style.flexBasis = '55px';
              }

              return (
                <VariantSelectorItem
                  key={optionOneValue}
                  itemStyles={style}
                  optionValue={optionOneValue}
                  checked={selectedOptionOne === optionOneValue}
                  disabled={false}
                  options={{level: 'one'}}
                />
              );
            })}
          </RadioGroup.Root>
        </div>
      )}

      {renderOptionTwo && (
        <div>
          {variantOptionTwoName && (
            <div className="text-[16px] text-gray-800 mb-1 pt-2">{variantOptionTwoName}</div>
          )}
          <RadioGroup.Root
            value={selectedOptionTwo}
            name="variantOptionTwo"
            onValueChange={handleOptionTwoChange}
            className={`flex flex-wrap gap-2 ${isWideFormat ? 'md:w-[90%]' : 'md:w-[70%]'}`}>
            {optionTwoKeyValues
              ?.filter(([_, status]) => status?.enabled)
              ?.map(([optionTwoValue, _]) => {
                return (
                  <VariantSelectorItem
                    key={optionTwoValue}
                    optionValue={optionTwoValue}
                    checked={selectedOptionTwo === optionTwoValue}
                    disabled={false}
                    options={{level: 'two'}}
                    className="flex-grow flex-shrink-0 basis-[calc(25%-0.5rem)]"
                  />
                );
              })}
          </RadioGroup.Root>
        </div>
      )}

      {renderOptionThree && (
        <div>
          {variantOptionThreeName && (
            <div className="text-[16px] text-gray-800 mb-1 pt-2">{variantOptionThreeName}</div>
          )}
          <RadioGroup.Root
            value={selectedOptionThree}
            name="variantOptionThree"
            onValueChange={handleOptionThreeChange}
            className="flex flex-wrap gap-2 md:w-[90%]">
            {optionThreeKeyValues
              ?.filter(([_, status]) => status?.enabled)
              .map(([optionThreeValue, status]) => {
                return (
                  <VariantSelectorItem
                    key={optionThreeValue}
                    optionValue={optionThreeValue}
                    checked={selectedOptionThree === optionThreeValue}
                    disabled={!status.enabled}
                    options={{
                      level: 'three',
                      totalOptions: Object.keys(optionsStatus.optionThree).length
                    }}
                    className="flex-grow flex-shrink-0 basis-[calc(25%-0.5rem)]"
                  />
                );
              })}
          </RadioGroup.Root>
        </div>
      )}
    </div>
  );
};

const VariantSelectorItem = forwardRef(
  ({itemStyles, optionValue, className, disabled, options = {}}, forwardedRef) => {
    const isArmorCoated = optionValue?.toLowerCase().includes('armor coated');

    const isLevelOne = options?.level === 'one';
    const isLevelTwo = options?.level === 'two';
    const isLevelThree = options?.level === 'three';

    const totalOptions = options?.totalOptions;

    const isClearance = isArmorCoated;

    const showClearenceBadge = (isClearance && isLevelTwo) || (isClearance && isLevelThree);

    const style = {
      fontSize: '15.5px',
      fontWeight: '600',
      fontFamily: 'monospace',
      transform: 'rotate(-2deg)',
      whiteSpace: 'nowrap',
      backgroundColor: 'rgba(121, 27,27, 0.9)',
      color: '#fff',
      padding: '2px 4px',
      borderRadius: '4px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      marginTop: '27.5px',
      right: '2.75rem'
    };

    if (totalOptions === 2) {
      style.right = '2.75rem';
    }

    if (totalOptions === 4) {
      style.fontSize = '11px';
      style.marginTop = '27px';
      style.right = '0rem';
      style.left = '1.15rem';
      style.backgroundColor = 'rgba(121, 27,27, 0.80)';
    }

    return (
      <>
        <RadioGroup.Item
          ref={forwardedRef}
          value={optionValue}
          style={itemStyles}
          disabled={disabled}
          name="variant"
          className={cn(
            `relative py-2 px-4 border cursor-pointer rounded-md text-[16px] text-gray-900 border-gray-300 bg-white hover:bg-gray-50 transition duration-200 ease-in`,
            `data-[state=checked]:bg-gray-100 data-[state=checked]:text-gray-900 data-[state=checked]:font-medium data-[state=checked]:border-gray-900`,
            `data-[state=disabled]:bg-gray-300 data-[state=disabled]:text-gray-400 data-[state=disabled]:cursor-not-allowed`,
            className
          )}>
          <label className="cursor-pointer whitespace-normal">{optionValue}</label>

          {showClearenceBadge && (
            <strong className="absolute" style={style}>
              ^ Clearance!&nbsp;
            </strong>
          )}
        </RadioGroup.Item>
      </>
    );
  }
);

const SingleLevelVariantSelector = () => {
  let {sku, variants, selectedVariantSku, relatedVariantsOptions, changeVariant, pagePath} =
    useProductProvider();

  const hideVariantOptionsForSingleSkuNoVariants = variants?.length === 1;
  if (hideVariantOptionsForSingleSkuNoVariants) return;

  const isSpeakerWireSku = sku?.startsWith('SKSPK');
  const isMarineSubSku = sku?.startsWith('SKM8') || selectedVariantSku?.startsWith('SKM10');

  const handleChange = (sku) => {
    changeVariant(sku);
  };

  let variantOneKey;
  if (relatedVariantsOptions) {
    variantOneKey = relatedVariantsOptions?.[0]?.variant_option_one_name;
  } else {
    if (isSpeakerWireSku) {
      variantOneKey = 'Length (feet)';
    } else if (isMarineSubSku) {
      variantOneKey = 'Coil Configuration & Color';
    } else {
      variantOneKey = 'Coil Configuration';
    }
  }

  if (variantOneKey === 'Size' && pagePath?.includes('speaker')) {
    variantOneKey = 'Available Sizes';
  }

  let variantOptions = variants?.map((variant) => {
    const variantSku = variant?.sku;
    const relatedVariantOption = relatedVariantsOptions?.find((opt) => opt.sku === variantSku);
    const speakerWireLength = variantSku?.includes('30FT') ? '30 FT' : '100 FT';

    const subIsBlack = variantSku?.includes('WBD');
    const subIsWhite = variantSku?.includes('WD');
    const subCoilIsD2 = variantSku?.includes('D2');
    const subCoilIsD4 = variantSku?.includes('D4');
    const marineSubBlackD2 = subIsBlack && subCoilIsD2 ? 'Dual 2Ω (Black)' : null;
    const marineSubBlackD4 = subIsBlack && subCoilIsD4 ? 'Dual 4Ω (Black)' : null;
    const marineSubWhiteD2 = subIsWhite && subCoilIsD2 ? 'Dual 2Ω (White)' : null;
    const marineSubWhiteD4 = subIsWhite && subCoilIsD4 ? 'Dual 4Ω (White)' : null;
    const marineSubCoilAndColor =
      marineSubBlackD2 || marineSubBlackD4 || marineSubWhiteD2 || marineSubWhiteD4;

    let variantDisplayName;
    if (relatedVariantOption) {
      variantDisplayName = relatedVariantOption.variant_option_one_value;
    } else if (isSpeakerWireSku) {
      variantDisplayName = speakerWireLength;
    } else if (isMarineSubSku) {
      variantDisplayName = marineSubCoilAndColor;
    } else {
      variantDisplayName = variant?.name;
    }
    return {
      name: variantDisplayName,
      sku: variantSku
    };
  });

  const justNames = variantOptions.map((item) => item.name);
  const sortedVariantOptions = sortVariantOptions(justNames);
  variantOptions = sortedVariantOptions.map((name) =>
    variantOptions.find((item) => item.name === name)
  );

  return (
    <>
      <div className="mt-1 mb-1">{variantOneKey}</div>
      <RadioGroup.Root value={selectedVariantSku} name="variant" onValueChange={handleChange}>
        <div className="flex items-center flex-wrap gap-2">
          {variantOptions?.map((variantOption) => {
            const variantSku = variantOption?.sku;
            const variantName = variantOption?.name;

            return (
              <RadioGroup.Item
                key={variantSku}
                name="variant"
                value={variantSku}
                className="p-2 border cursor-pointer rounded text-sm md:text-md text-gray-900 data-[state=checked]:bg-gray-900 data-[state=checked]:text-white transition duration-200 ease-in">
                <label className="cursor-pointer">{variantName}</label>
              </RadioGroup.Item>
            );
          })}
        </div>
      </RadioGroup.Root>
    </>
  );
};
